// Sugalarm — confetti burst (cycle 34.18).
//
// Pure-CSS particles for celebratory feedback on successful log
// actions. No external dependency, no canvas — each particle is a
// tiny `<span>` that reads `--drift` + `--spin` CSS variables from
// inline style and animates via the global `@keyframes confettiFall`
// declared in theme/animations.css.
//
// Usage:
//   <Confetti burst={burstCount} />
// where `burst` is a monotonically increasing counter. Each new
// value spawns a fresh set of particles; the previous batch is torn
// down automatically when the animation completes.

import { useEffect, useMemo, useState } from "react";

interface Props {
  /** Increment this counter to fire a new burst. `0` (or null) is
   *  the "no burst" sentinel — nothing renders. */
  burst: number | null;
  /** Optional override for the particle palette. Defaults to a
   *  zone-friendly Sugalarm mix. */
  colors?: string[];
  /** Number of particles per burst. Default 50 reads cheerfully on
   *  a phone viewport without thrashing the compositor. */
  count?: number;
}

interface Particle {
  id: string;
  left: number; // % across viewport
  drift: number; // px horizontal drift over the fall
  spin: number; // deg total rotation
  delay: number; // ms initial delay
  duration: number; // ms fall length
  color: string;
  size: number; // px width of the rectangle
  aspect: number; // height multiplier
}

const DEFAULT_COLORS = [
  "#fbbf24", // yellow accent
  "#ff6b6b", // urgent red
  "#5b9dff", // dark-theme accent blue
  "#6dd4a3", // ok green
  "#fde68a", // soft yellow
  "#f5b942", // warn orange
];

const FALL_TOTAL_MS = 2400;
const CLEANUP_MS = FALL_TOTAL_MS + 800;

function makeParticles(burstId: number, palette: string[], n: number): Particle[] {
  const out: Particle[] = [];
  for (let i = 0; i < n; i += 1) {
    const direction = Math.random() < 0.5 ? -1 : 1;
    out.push({
      id: `${burstId}-${i}`,
      left: 10 + Math.random() * 80,
      drift: direction * (40 + Math.random() * 220),
      spin: (Math.random() < 0.5 ? -1 : 1) * (360 + Math.random() * 720),
      delay: Math.random() * 250,
      duration: 1600 + Math.random() * 1000,
      color: palette[Math.floor(Math.random() * palette.length)]!,
      size: 6 + Math.random() * 6,
      aspect: 1.4 + Math.random() * 0.8,
    });
  }
  return out;
}

export function Confetti({
  burst,
  colors = DEFAULT_COLORS,
  count = 50,
}: Props) {
  const [particles, setParticles] = useState<Particle[]>([]);
  // Capture the palette identity so we don't regenerate particles on
  // every parent re-render (only when the burst counter advances).
  const palette = useMemo(() => colors, [colors.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!burst) return;
    setParticles(makeParticles(burst, palette, count));
    const t = window.setTimeout(() => setParticles([]), CLEANUP_MS);
    return () => window.clearTimeout(t);
  }, [burst, palette, count]);

  if (particles.length === 0) return null;

  return (
    <div
      aria-hidden
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        overflow: "hidden",
        zIndex: 300,
      }}
    >
      {particles.map((p) => (
        <span
          key={p.id}
          style={{
            position: "absolute",
            top: 0,
            left: `${p.left}%`,
            width: p.size,
            height: p.size * p.aspect,
            background: p.color,
            borderRadius: 2,
            opacity: 0.95,
            // Custom properties consumed by the global @keyframes.
            ["--drift" as string]: `${p.drift}px`,
            ["--spin" as string]: `${p.spin}deg`,
            animation: `confettiFall ${p.duration}ms cubic-bezier(0.22, 0.61, 0.32, 1) ${p.delay}ms forwards`,
            willChange: "transform, opacity",
          }}
        />
      ))}
    </div>
  );
}
